import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Sparkles, Mail, Lock, User, Globe, Send, ArrowLeft, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { clipperLogin, clipperSignup } from '../../lib/api';

const SITE_LOGO =
  'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';

export default function ClipAuthPage({ mode = 'login' }) {
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    country: '',
    telegram_handle: '',
    tiktok: '',
    instagram: '',
    facebook: '',
  });
  const [submitting, setSubmitting] = useState(false);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function splitHandles(raw) {
    if (!raw) return [];
    return raw
      .split(/[\s,\n]+/)
      .map((h) => h.trim())
      .filter(Boolean)
      .slice(0, 10);
  }

  async function onSubmit(e) {
    e?.preventDefault?.();
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email'); return;
    }
    if (!form.password || form.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    if (isSignup && !form.name.trim()) {
      toast.error('Please enter your name'); return;
    }
    setSubmitting(true);
    try {
      if (isSignup) {
        const payload = {
          name: form.name,
          email: form.email,
          password: form.password,
          country: form.country,
          telegram_handle: form.telegram_handle,
          social_handles: {
            tiktok: splitHandles(form.tiktok),
            instagram: splitHandles(form.instagram),
            facebook: splitHandles(form.facebook),
          },
        };
        await clipperSignup(payload);
        toast.success('Welcome! Your clipper account is ready.');
      } else {
        await clipperLogin({ email: form.email, password: form.password });
        toast.success('Welcome back!');
      }
      navigate('/clip/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = err?.message || (isSignup ? 'Signup failed' : 'Login failed');
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        msg = detail[0].msg;
      }
      toast.error(String(msg));
    } finally { setSubmitting(false); }
  }

  return (
    <main className="min-h-screen text-white bg-[hsl(0_0%_4%)] relative noise-overlay flex flex-col" data-testid="clip-auth-page">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 50% 0%, rgba(245,185,66,0.16), transparent 70%), radial-gradient(50% 50% at 80% 100%, rgba(225,29,46,0.12), transparent 70%)',
        }}
      />
      <header className="relative z-10 border-b border-white/5">
        <div className="container-x py-4 flex items-center justify-between gap-3">
          <Link to="/clip" className="flex items-center gap-2.5">
            <img src={SITE_LOGO} alt="AlveeFx" className="h-8 w-8 rounded-full object-cover ring-1 ring-[rgba(245,185,66,0.42)]" />
            <span className="font-display uppercase tracking-[0.18em] text-sm sm:text-base">AlveeFx</span>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] px-2 py-0.5 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)]">Clip Program</span>
          </Link>
          <Link to="/clip" className="text-xs text-muted-foreground hover:text-white inline-flex items-center gap-1.5" data-testid="back-to-clip">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        </div>
      </header>

      <section className="relative z-10 flex-1 flex items-center justify-center px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="card-surface">
            <CardContent className="p-6 sm:p-7">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(43_92%_56%)]/40 bg-[rgba(245,185,66,0.06)] mb-3">
                  <Sparkles className="h-3 w-3 text-[hsl(43_92%_56%)]" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[hsl(43_92%_56%)] font-semibold">Clipper Portal</span>
                </div>
                <h1 className="font-display uppercase text-2xl sm:text-3xl">
                  {isSignup ? 'Create your account' : 'Welcome back'}
                </h1>
                <p className="mt-2 text-xs text-muted-foreground">
                  {isSignup
                    ? 'Instant approval — start submitting clips in 30 seconds.'
                    : 'Log in to manage clips and request withdrawals.'}
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-3.5">
                {isSignup && (
                  <Field icon={User} label="Full Name" testid="field-name">
                    <Input value={form.name} onChange={(e) => up('name', e.target.value)} placeholder="Your full name" data-testid="clip-name" />
                  </Field>
                )}

                <Field icon={Mail} label="Email" testid="field-email">
                  <Input type="email" autoComplete="email" value={form.email} onChange={(e) => up('email', e.target.value)} placeholder="you@email.com" data-testid="clip-email" />
                </Field>

                <Field icon={Lock} label="Password" testid="field-password">
                  <Input type="password" autoComplete={isSignup ? 'new-password' : 'current-password'} value={form.password} onChange={(e) => up('password', e.target.value)} placeholder={isSignup ? 'At least 6 characters' : 'Your password'} data-testid="clip-password" />
                </Field>

                {isSignup && (
                  <>
                    <Field icon={Globe} label="Country (optional)">
                      <Input value={form.country} onChange={(e) => up('country', e.target.value)} placeholder="e.g. Bangladesh" data-testid="clip-country" />
                    </Field>
                    <Field icon={Send} label="Telegram handle (optional)">
                      <Input value={form.telegram_handle} onChange={(e) => up('telegram_handle', e.target.value)} placeholder="@yourhandle" data-testid="clip-telegram" />
                    </Field>

                    {/* Social handles — required for verification but can be added later */}
                    <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 space-y-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(43_92%_72%)] font-semibold">
                          Social handles (recommended)
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          Add the accounts you&apos;ll post clips from. Required before submitting your first clip.
                          You can also add them later from your Dashboard. Multiple handles per platform allowed (comma-separated).
                        </div>
                      </div>
                      <Field icon={AtSign} label="TikTok handles (optional)">
                        <Input value={form.tiktok} onChange={(e) => up('tiktok', e.target.value)} placeholder="@your_tiktok, @second" data-testid="clip-tiktok" />
                      </Field>
                      <Field icon={AtSign} label="Instagram handles (optional)">
                        <Input value={form.instagram} onChange={(e) => up('instagram', e.target.value)} placeholder="@your_ig, @second_ig" data-testid="clip-instagram" />
                      </Field>
                      <Field icon={AtSign} label="Facebook page handles (optional)">
                        <Input value={form.facebook} onChange={(e) => up('facebook', e.target.value)} placeholder="page.username" data-testid="clip-facebook" />
                      </Field>
                    </div>
                  </>
                )}

                <Button type="submit" disabled={submitting} size="lg"
                  className="cta-sheen border-0 w-full h-11 font-semibold mt-2 gap-2"
                  data-testid="clip-submit">
                  {submitting
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> {isSignup ? 'Creating…' : 'Logging in…'}</>
                    : <><Sparkles className="h-4 w-4" /> {isSignup ? 'Create account' : 'Log in'}</>}
                </Button>
              </form>

              <div className="mt-5 text-center text-xs text-muted-foreground">
                {isSignup ? (
                  <>Already have an account? <Link to="/clip/login" className="text-[hsl(43_92%_72%)] hover:underline" data-testid="link-login">Log in</Link></>
                ) : (
                  <>New here? <Link to="/clip/signup" className="text-[hsl(43_92%_72%)] hover:underline" data-testid="link-signup">Create your account</Link></>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-4 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Your data is private. We only ask for what we need to pay you.
          </div>
        </motion.div>
      </section>
    </main>
  );
}

function Field({ icon: Icon, label, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
